import { animate, style, transition, trigger } from '@angular/animations'
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'
import { Control, defaults, ScaleLine } from 'ol/control'
import {
  boundingExtent,
  createEmpty,
  extend,
  Extent,
  getCenter,
} from 'ol/extent'
import Feature, { FeatureLike } from 'ol/Feature'
import { Circle, Point, Polygon } from 'ol/geom'
import Select from 'ol/interaction/Select'
import TileLayer from 'ol/layer/Tile'
import VectorLayer from 'ol/layer/Vector'
import Map from 'ol/Map'
import { transform } from 'ol/proj'
import VectorSource from 'ol/source/Vector'
import XYZ from 'ol/source/XYZ'
import View from 'ol/View'
import { Subscription } from 'rxjs'

import { environment } from 'src/environments/environment'
import { API_BASE_URL } from '@/shared/services/service-clients'
import { MapBrowserEvent, Overlay } from 'ol'
import { Interaction } from 'ol/interaction'
import {
  LngLat,
  ProjectionEnum,
} from 'src/app/modules/commons/models/geography'
import { OverlayComponent } from '../overlay/overlay.component'

const MAP_DEFAULT_OFFSET = [100, 100, 100, 100]
const MAP_FIT_OPT = { padding: MAP_DEFAULT_OFFSET, duration: 500 }

export interface BaseMapEvent {
  features: FeatureLike[]
}

export interface MapHandlers {
  layoutName: string
  fn: Function
}

interface MapLayerEntry {
  layer: VectorLayer<VectorSource>
  source: VectorSource
  handler?: Function
}

@Component({
  selector: 'app-map-base',
  imports: [OverlayComponent],
  templateUrl: './map-base.component.html',
  styleUrls: ['./map-base.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('300ms', style({ opacity: 0 }))]),
    ]),
  ],
})
export class MapBaseComponent implements OnInit, OnDestroy, AfterViewInit {
  @Output()
  clickOnMap = new EventEmitter<MapBrowserEvent<any>>()

  @Output()
  clickOnFeatures = new EventEmitter<BaseMapEvent>()

  @Input()
  registerMapHandlers!: MapHandlers[]

  @Input()
  loading!: boolean

  // private readonly _maxFeaturesAllowed: number = 200;
  private _buildingListSubs!: Subscription
  private _viewportSubs!: Subscription

  private _baseUrl: string
  private _tileLayer!: TileLayer<XYZ>
  private _tileLayerIGN!: TileLayer<XYZ>
  private _tileSource!: XYZ
  private _tileSourceIGN!: XYZ
  private _view!: View
  private _map!: Map
  private _selectSingleClick!: Select
  private _layers: Record<string, MapLayerEntry> = {}
  private _baseCenter!: number[]
  private _featuresById: any = {}
  private _regGreyscale: any
  // private _viewport: WindowSize;

  public mapId!: string

  constructor(@Inject(API_BASE_URL) baseUrl?: string) {
    this._baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : ''
  }

  ngOnInit() {
    this._initMeta()
  }

  ngAfterViewInit() {
    this._initMap()
    this._initEvent()
  }

  private _initMeta() {
    const { map } = environment
    this.mapId = `map-${new Date().getTime()}-base`

    this._baseCenter = transform(
      map.center,
      ProjectionEnum.worldGeodeticSystem1984,
      ProjectionEnum.webMercator
    )

    this._view = new View({
      center: this._baseCenter,
      zoom: map.zoom,
      minZoom: map.minZoom,
    })

    this._tileSource = new XYZ({
      url: `${this._baseUrl}/api/Carte/GetTuile/{z}/{x}/{y}.png`,
      cacheSize: 512,
    })

    this._tileLayer = new TileLayer({
      source: this._tileSource,
    })
    this._tileSourceIGN = new XYZ({
      url: `https://data.geopf.fr/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=ORTHOIMAGERY.ORTHOPHOTOS&TILEMATRIXSET=PM&TILEMATRIX={z}&TILECOL={x}&TILEROW={y}&STYLE=normal&FORMAT=image/jpeg`,
      cacheSize: 512,
    })

    this._tileLayerIGN = new TileLayer({
      source: this._tileSourceIGN,
    })
  }

  private _initMap() {
    this._setMap()

    // this._buildingListSubs = this._buildingService.buildings$.subscribe(
    //   (buildings) => {
    //     if (buildings && buildings.length) this.building = buildings[0]
    //     // this._setMap()
    //     this._setBuilding()
    //     this._setView()
    //   }
    // )
  }

  private _setView() {
    // if (!this.building) return
    // const { map } = environment
    // this._baseCenter = transform(
    //   map.center,
    //   ProjectionEnum.worldGeodeticSystem1984,
    //   ProjectionEnum.webMercator
    // )
    // const zoom = Math.floor(
    //   (this.building.min_zoom + this.building.max_zoom) / 2
    // )
    // this._view.setCenter(this._baseCenter)
    // this._view.setMinZoom(this.building.min_zoom)
    // this._view.setMaxZoom(this.building.max_zoom)
    // this._view.setZoom(zoom)
  }

  private _setMap() {
    if (this._map) return
    this._map = new Map({
      controls: defaults({
        attributionOptions: {
          collapsible: false,
        },
      }).extend([new ScaleLine({ units: 'metric' })]),
      target: this.mapId,
      layers: [this._tileLayerIGN, this._tileLayer],
      view: this._view,
    })
  }

  /*     setViewport(vp: WindowSize) {
        console.log('Viewport', vp);
        this._viewport = vp;
    } */

  private _initEvent() {
    // click event
    this._map.on('click', (e) => this.onMapClick(e))
  }

  public onMapClick(event: MapBrowserEvent<any>) {
    const features: FeatureLike[] = []
    const layers = this.getLayersKeysObject()

    // on emet le click
    this.clickOnMap.emit(event)

    // trie les features cliquées selon leur layer
    this._map.forEachFeatureAtPixel(
      event.pixel,
      (f, l) => {
        features.push(f)
        if (!l) return
        const name = l.get('layerName')
        if (l) {
          if (!layers[name]) layers[name] = []
          layers[name].push(f)
        }
      },
      { hitTolerance: 15 }
    )

    // On appele le click feature
    this.clickOnFeatures.emit({ features })

    // On appelle le handler pour chaque layer
    Object.keys(layers).forEach((key) => {
      const handler = this.getLayerHandler(key)
      if (handler) handler(layers[key])
    })
  }

  public setVisible(layerName: string, visibility: boolean) {
    if (layerName) {
      const layer = this.getLayer(layerName)
      if (layer) layer.setVisible(visibility)
    }
  }

  public getMap() {
    return this._map
  }

  public getView() {
    return this._view
  }

  public addOverlay(overlay: Overlay) {
    this._map.addOverlay(overlay)
  }

  public removeOverlay(overlay: Overlay) {
    this._map.removeOverlay(overlay)
  }

  public addControl(control: Control) {
    this._map.addControl(control)
  }

  public addControls(controls: Control[]) {
    for (let control of controls) {
      this.addControl(control)
    }
  }

  public createLayer(layerName: string, layerStyle: any, handler?: Function) {
    const source = new VectorSource()
    const layer = new VectorLayer({ source, style: layerStyle })
    layer.set('layerName', layerName)
    this._layers[layerName] = { layer, source, handler }
    this._map.addLayer(layer)
  }

  public addFeaturesOnLayer(layerName: string, features: Feature<any>[]) {
    const { source } = this._layers[layerName]
    this.indexFeaturesOnLayer(layerName, features)
    source.addFeatures(features)
  }

  public setFeaturesOnLayer(layerName: string, features: Feature<any>[]) {
    const { source } = this._layers[layerName]
    this.clearFeaturesOnLayer(layerName)
    this.indexFeaturesOnLayer(layerName, features)
    source.addFeatures(features)
  }

  public clearFeaturesOnLayer(layerName: string) {
    const { source } = this._layers[layerName]
    if (this._featuresById[layerName]) {
      delete this._featuresById[layerName]
    }
    source.clear()
  }

  public indexFeaturesOnLayer(layerName: string, features: Feature<any>[]) {
    if (!this._featuresById[layerName]) {
      this._featuresById[layerName] = {}
    }
    for (let feature of features) {
      const id = feature.getId()
      if (id !== undefined) this._featuresById[layerName][id] = feature
    }
  }

  public addInteraction(interaction: Interaction) {
    this._map.addInteraction(interaction)
  }

  public removeInteraction(interaction: Interaction) {
    this._map.removeInteraction(interaction)
  }

  public fit(features: Feature<any>[], options = {}) {
    // No features, nothing to fit.
    if (!features?.length) return

    // On build les options
    const opts = { ...MAP_FIT_OPT, ...options }

    // Extract an array of coordinates from Features array.
    const coordinates = features.map((f) => {
      const point = f.getGeometry() as Point
      return point.getCoordinates()
    })

    // Create an extent from coordinates.
    const extent = boundingExtent(coordinates)

    // Fit map to the extent.
    this._view.fit(extent, opts)
  }

  public getFeatureById(layerName: string, id: string | number) {
    return this._featuresById[layerName]?.[id]
  }

  public getSource(layerName: string) {
    return this._layers[layerName]?.source
  }

  public getLayer(layerName: string) {
    return this._layers[layerName]?.layer
  }

  public getLayerHandler(layerName: string): Function | undefined {
    return this._layers[layerName]?.handler
  }

  public getLayerFeatures(layerName: string) {
    const source = this.getSource(layerName)
    return source ? source.getFeatures() : null
  }

  public getLayersKeysObject(): Record<string, FeatureLike[]> {
    const obj: Record<string, FeatureLike[]> = {}
    Object.keys(this._layers).forEach((n) => (obj[n] = []))
    return obj
  }

  public forEachFeatureInLayer(layerName: string, fn: Function) {
    const features = this.getLayerFeatures(layerName)
    if (!features) {
      return
    }
    for (let feature of features) {
      fn(feature)
    }
    this.getSource(layerName).changed()
  }

  public getBaseCenter() {
    return this._baseCenter
  }

  public center() {
    this.centerOnFeature(null)
  }

  public centerOnLngLat(coords: LngLat) {
    const newPoint = transform(
      [coords.lng, coords.lat],
      ProjectionEnum.worldGeodeticSystem1984,
      ProjectionEnum.webMercator
    )

    const center = [newPoint[0], newPoint[1]]

    this._view.animate({
      center,
      duration: 300,
    })
  }

  public centerOnFeature(feature: Feature<any> | null, shape?: string) {
    let center

    if (!this._map) {
      return
    }

    if (!feature) {
      if (this._baseCenter) {
        this._view.setCenter(this._baseCenter)
      }
      return
    }

    if (shape === 'Circle') {
      const geometry = feature.getGeometry() as Circle
      center = geometry.getCenter()
    } else if (shape === 'Polygon') {
      const geometry = feature.getGeometry() as Polygon
      const extent = geometry.getExtent() as Extent
      center = getCenter(extent)
    } else {
      const geometry = feature.getGeometry() as Point
      center = geometry.getCoordinates()
    }

    this._view.animate({
      center,
      duration: 300,
    })
  }

  public centerOnFeatures(features: Feature<any>[]) {
    if (features?.length) {
      const extent = createEmpty()
      for (let feature of features) {
        extend(extent, feature.getGeometry().getExtent())
        this._view.animate({
          center: getCenter(extent),
          duration: 300,
        })
      }
    }
  }

  public fitOnFeature(feature: Feature<any>) {
    this._view.fit(feature.getGeometry().getExtent(), {
      padding: MAP_DEFAULT_OFFSET,
    })
  }

  public fitOnFeatures(features: Feature<any>[]) {
    if (features?.length) {
      const extent = createEmpty()
      for (let feature of features) {
        extend(extent, feature.getGeometry().getExtent())
        this._view.fit(extent, { padding: MAP_DEFAULT_OFFSET })
      }
    }
  }

  ngOnDestroy() {
    if (!!this._buildingListSubs) this._buildingListSubs.unsubscribe()
    // if(!!this._viewportSubs) this._viewportSubs.unsubscribe();
  }
}
